/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { ChevronLeft, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';

export interface FiltersButtonProps {
    /** Callback when button is clicked */
    onClick: () => void;
    /** Whether the filters panel is currently shown */
    isActive?: boolean;
    /** Number of selected filters currently applied */
    selectedFiltersCount?: number;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Toggles the filters panel on the PLP.
 *
 * Per Figma:
 * - Closed state (node 33746:195818, "Filter sidebar=False"): `settings-2` icon
 *   — communicates "open the filter panel".
 * - Open state (node 12165:93241): `chevron-left` icon — communicates "collapse
 *   the panel back to the left".
 * - Uppercase "Filters" label, Chesna Grotesk Semi Bold, 12px, 1.8px tracking.
 * - Borderless / ghost variant in both states.
 *
 * `type="button"` is set explicitly: native <button> defaults to `type="submit"`,
 * which can swallow clicks if the toolbar is ever rendered inside a <form>.
 */
export default function FiltersButton({
    onClick,
    isActive = false,
    selectedFiltersCount = 0,
    className,
}: FiltersButtonProps) {
    const { t } = useTranslation();
    const filtersLabel = t('categoryRefinements:filtersButtonLabel');
    const normalizedSelectedFiltersCount = Math.max(0, Math.floor(selectedFiltersCount));
    const hasSelectedFilters = normalizedSelectedFiltersCount > 0;
    const filtersAriaLabel = hasSelectedFilters
        ? t('categoryRefinements:filtersButtonLabelWithCount', {
              label: filtersLabel,
              count: normalizedSelectedFiltersCount,
          })
        : filtersLabel;

    const Icon = isActive ? ChevronLeft : Settings2;

    return (
        <Button
            type="button"
            variant="ghost"
            onClick={onClick}
            className={cn(
                'h-9 cursor-pointer gap-1 rounded-xs px-3 py-2 text-xs font-semibold uppercase tracking-button text-foreground hover:bg-secondary w-fit',
                className
            )}
            aria-label={filtersAriaLabel}
            aria-pressed={isActive}
            aria-expanded={isActive}>
            <Icon className="size-4" aria-hidden />
            <span>{filtersLabel}</span>
            {hasSelectedFilters && (
                <span
                    className="ml-1 inline-flex min-w-5 items-center justify-center rounded-xs bg-secondary px-1 text-xs font-semibold tracking-normal text-secondary-foreground"
                    aria-hidden>
                    {normalizedSelectedFiltersCount}
                </span>
            )}
        </Button>
    );
}
